/**
 * Hook for handling Event Survey form submission with event pooling support
 *
 * This hook encapsulates the complex logic for building the request body
 * from form values and submitting it via create or update mutations.
 *
 * **Event Pooling**: For paid events requiring payment, the response is "pooled"
 * (held in memory) instead of being sent to the backend immediately. The pooled
 * response is returned via `responseBody` and should be submitted after successful
 * payment processing.
 *
 * When pooling an update (surveyResponse.id exists), the pooled response includes
 * all existing response fields (id, surveyId, token) to ensure the backend treats
 * it as an update rather than creating a new survey response.
 *
 * @returns {Object} result
 * @returns {Function} result.handleSave - Function to handle form submission
 * @returns {IEventSurveyResponse | null} result.responseBody - Pooled response body when payment is needed, null otherwise
 */

import type { UseMutationResult } from '@tanstack/react-query';
import * as React from 'react';
import { toast } from 'sonner';
import type {
	IEventRegistrationSurvey,
	IEventSurveyResponse,
	IPublicFileAttachment,
	IRecipient,
} from '../../../../../../../models/Api';
import { computeVisibleFields } from '../../../../../shared/hooks/useConditionalVisibility';

interface ITicketHolder {
	id?: string; // Unique ID from backend or generated UUID
	ticketId: string;
	ticketName: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	isPaid?: boolean; // Whether this ticket has been paid for
	ticketItemIndex?: number | null; // Which physical ticket (table) this seat belongs to; undefined for single-seat tickets
}

interface IFormValues {
	isAttending: boolean | null;
	isPaid: boolean;
	bringingGuest?: boolean;
	email?: string;
	firstName?: string;
	lastName?: string;
	phone?: string;
	guests: IRecipient[];
	customProperties: Record<string, string | IPublicFileAttachment[]>;
	ticketSelections?: Record<string, number>;
	ticketHolders?: ITicketHolder[];
}

interface UseEventSurveyFormSubmissionProps {
	survey: IEventRegistrationSurvey;
	surveyResponse: IEventSurveyResponse;
	primaryTicketHolderId: string | null;
	processingPayment: React.MutableRefObject<boolean>;
	pendingAttendanceSelection: React.MutableRefObject<boolean | null>;
	getValues: () => IFormValues;
	urlParams: { token?: string };
	createResponseMutation: UseMutationResult<
		IEventSurveyResponse,
		unknown,
		{ body: IEventSurveyResponse; token: string }
	>;
	updateResponseMutation: UseMutationResult<
		IEventSurveyResponse,
		unknown,
		{ accessToken: string; body: IEventSurveyResponse }
	>;
	setIsSubmitting: (loading: boolean) => void;
}

export const useEventSurveyFormSubmission = ({
	survey,
	surveyResponse,
	primaryTicketHolderId,
	processingPayment,
	pendingAttendanceSelection,
	getValues,
	urlParams,
	createResponseMutation,
	updateResponseMutation,
	setIsSubmitting,
}: UseEventSurveyFormSubmissionProps) => {
	const [responseBody, setResponseBody] = React.useState<IEventSurveyResponse | null>(null);

	const handleSave = React.useCallback(() => {
		setIsSubmitting(true); // Start loading indicator
		const values = getValues();

		const body: IEventSurveyResponse = {
			anonymousGuestCount: 0,
			attendee: {
				email: values.email.trim(),
				firstName: values.firstName.trim(),
				lastName: values.lastName.trim(),
				phone: values.phone?.trim(),
				// Preserve the isPaid flag from the existing attendee
				isPaid: surveyResponse?.attendee?.isPaid,
			},
			guests: [],
			isAttending: values.isAttending,
		};

		/**
		 * Handle ticket holders for paid events with tiered ticketing
		 *
		 * The primaryTicketHolderId indicates which ticket holder is the primary attendee.
		 * We preserve holder IDs to maintain stable backend mappings.
		 */
		if (survey?.isPaidEvent && values.ticketHolders && values.ticketHolders.length > 0) {
			// Map primary ticket holder to attendee with their ticket (find by ID)
			const primaryHolder = values.ticketHolders.find(h => h.id === primaryTicketHolderId);
			if (primaryHolder) {
				const primaryTicket = survey.attendeeOptions?.eventTickets?.find(t => t.id === primaryHolder.ticketId);
				body.attendee = {
					id: primaryHolder.id, // Preserve backend ID
					email: primaryHolder.email?.trim() || '',
					firstName: primaryHolder.firstName?.trim() || '',
					lastName: primaryHolder.lastName?.trim() || '',
					phone: primaryHolder.phone?.trim(),
					isPaid: primaryHolder.isPaid || surveyResponse?.attendee?.isPaid || false,
					ticket: primaryTicket,
					ticketItemIndex: primaryHolder.ticketItemIndex,
				};
			}

			// Map remaining ticket holders to guests array (skip primary, filter incomplete)
			body.guests = values.ticketHolders
				.filter(holder => holder.id !== primaryTicketHolderId) // Skip primary by ID
				.map((holder, index) => {
					const ticket = survey.attendeeOptions?.eventTickets?.find(t => t.id === holder.ticketId);

					return {
						id: holder.id, // Preserve backend ID
						firstName: holder.firstName?.trim() || 'Guest',
						lastName: holder.lastName?.trim() || `Attendee ${index + 1}`,
						email: holder.email?.trim(),
						phone: holder.phone?.trim(),
						isPaid: holder.isPaid || false,
						ticket,
						ticketItemIndex: holder.ticketItemIndex,
					};
				});
		} else if (values.bringingGuest) {
			// Legacy guest handling for non-tiered events
			const guests = values.guests.filter(guest => guest.firstName?.trim() && guest.lastName?.trim());

			// Preserve isPaid flags for existing guests and set false for new ones
			const processedGuests = guests.map((guest, index) => {
				// If this guest exists in the original response, preserve their isPaid status
				const existingGuest = surveyResponse?.guests?.[index];
				return {
					...guest,
					isPaid: existingGuest?.isPaid || false,
				};
			});

			body.guests = processedGuests;
			body.anonymousGuestCount = values.guests.length - guests.length;

			// For paid events, convert anonymous guests to named guests
			if (survey?.isPaidEvent && body.anonymousGuestCount > 0) {
				const newGuests = [];
				for (let i = 0; i < body.anonymousGuestCount; i++) {
					newGuests.push({
						firstName: `Guest`,
						lastName: `Attending ${i + 1 + processedGuests.length}`,
						isPaid: false, // New guests are not paid
					});
				}
				body.guests = [...body.guests, ...newGuests];
				body.anonymousGuestCount = 0;
			}
		}

		if (Object.values(values.customProperties).length) {
			// Skip required-field checks for fields hidden by surveyForm visibility rules.
			// Fields not represented in surveyForm fall through to the standard check
			// (matches the yup schema in useEventSurveyValidation).
			const surveyPartFieldIds = survey.surveyForm?.surveyParts?.length
				? new Set(
						survey.surveyForm.surveyParts
							.map(part => (part as { fieldId?: string }).fieldId)
							.filter((id): id is string => !!id)
					)
				: undefined;
			const visibleFieldIds = survey.surveyForm?.surveyParts?.length
				? computeVisibleFields(survey.surveyForm, values.customProperties)
				: undefined;
			const fieldsWithMissingInput = survey.customForm?.fields?.filter(x => {
				if (x.isOptional || x.isHidden) return false;
				if (values.customProperties[x.id]) return false;
				// If surveyForm governs this field and visibility rules hide it, skip.
				if (surveyPartFieldIds?.has(x.id) && visibleFieldIds && !visibleFieldIds.has(x.id)) return false;
				return true;
			});
			if (fieldsWithMissingInput?.length && body.isAttending) {
				toast.error(`Please fill out all required fields`);
				return;
			}
			body.customProperties = values.customProperties;
		}

		// Only process payment if user is attending - "not attending" RSVPs don't require payment
		if (survey?.isPaidEvent && body.isAttending) {
			processingPayment.current = !body.attendee.isPaid || body.guests.some(guest => !guest.isPaid);

			/**
			 * EVENT POOLING PATTERN:
			 * For paid events requiring payment, we use an "event pooling" pattern:
			 *
			 * 1. User fills form and clicks submit
			 * 2. Instead of saving to DB, we "pool" (store) the response in memory
			 * 3. User is redirected to Stripe checkout for payment
			 * 4. After successful payment, the pooled response is sent to backend
			 * 5. Backend saves the response and marks attendees as paid
			 *
			 * This prevents creating incomplete database records for unpaid registrations
			 * and ensures payment atomicity with the survey response.
			 */
			if (processingPayment.current) {
				// Store the user's attendance selection for payment processing
				pendingAttendanceSelection.current = values.isAttending;

				// For updates (surveyResponse.id exists), merge the full existing response with the new form data.
				// This preserves critical backend fields that the backend needs to:
				// - surveyId: Identify which survey this response belongs to (prevents creating new survey)
				// - id: Identify this as an update to an existing response (not a new response)
				// - token: Authorize the update operation
				//
				// Spread order matters: surveyResponse first, then body.
				// This ensures form data (body) overwrites editable fields (isAttending, attendee, guests)
				// while preserving non-editable metadata (id, surveyId, token) from surveyResponse.
				//
				// For new responses (no surveyResponse.id), only include the new form data.
				const pooledBody = surveyResponse?.id ? { ...surveyResponse, ...body } : body;

				setResponseBody(pooledBody);
				setIsSubmitting(false);
				return; // Don't save to DB yet, wait for payment
			}
		}
		// PUT/Update if we have an id
		if (surveyResponse?.id) {
			const payload = {
				...surveyResponse,
				...body,
			};
			updateResponseMutation.mutate({
				accessToken: urlParams.token,
				body: payload,
			});
			return;
		}

		// POST/Create
		createResponseMutation.mutate({ body, token: urlParams.token });
	}, [
		setIsSubmitting,
		getValues,
		survey,
		surveyResponse,
		primaryTicketHolderId,
		processingPayment,
		pendingAttendanceSelection,
		urlParams.token,
		createResponseMutation,
		updateResponseMutation,
	]);

	return { handleSave, responseBody };
};
